/**
 * AiShimmer — Reusable AI shimmer glow effect.
 *
 * Wraps any child element (button, icon, menu item) with an animated
 * radial-gradient glow that conveys "AI-powered". Use it around action
 * buttons for live mode, AI suggestions, smart actions, etc.
 *
 * Usage:
 *   <AiShimmer>
 *     <IconButton ... />
 *   </AiShimmer>
 *
 *   <AiShimmer size="lg" intensity="strong">
 *     <MaterialIcon name="auto_awesome" />
 *   </AiShimmer>
 */

import { Box } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import type React from 'react';

// Breathing glow animation — used by the button/icon variant, which grows a
// soft radial blob behind a small element.
const shimmerGlow = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.3); }
`;

// Edge-glow breathing — used by the fullWidth variant. Pulses opacity only
// (no scale) so the halo hugs the element's rounded edge instead of ballooning
// into a big blob the way a scaled radial fill does on a wide element.
const edgeGlow = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.85; }
`;

// `blur`/`spread` drive the `glow` treatment; `edge*` drive the `edge` one.
// The edge row for `strong` reproduces the values the fullWidth branch used to
// hardcode, so the dashboard composer is pixel-identical after the split.
const INTENSITY_MAP = {
  subtle: {
    blur: '4px',
    spread: '-3px',
    opacityMultiplier: 0.6,
    edgeBlur: '8px',
    edgeSpread: '0px',
    edgeInnerBlur: '3px',
  },
  default: {
    blur: '6px',
    spread: '-4px',
    opacityMultiplier: 1,
    edgeBlur: '12px',
    edgeSpread: '0px',
    edgeInnerBlur: '4px',
  },
  strong: {
    blur: '10px',
    spread: '-5px',
    opacityMultiplier: 1.2,
    edgeBlur: '16px',
    edgeSpread: '1px',
    edgeInnerBlur: '5px',
  },
} as const;

interface AiShimmerProps {
  children: React.ReactNode;
  /** Controls glow spread and brightness. @default 'default' */
  intensity?: keyof typeof INTENSITY_MAP;
  /** Whether the shimmer is active. When false, no glow is rendered. @default true */
  isActive?: boolean;
  /** Animation duration in seconds. @default 4.5 */
  duration?: number;
  /**
   * Stretch to fill the parent's width (block, not inline). Use when wrapping
   * a full-width element like the chat composer rather than a button/icon.
   * This is a LAYOUT concern only — for the visual treatment see `variant`.
   * @default false
   */
  fullWidth?: boolean;
  /**
   * Visual treatment.
   * - `glow` — a blurred radial blob that breathes behind the element. Reads as
   *   a soft wash; the child's own shape is lost inside it.
   * - `edge` — a box-shadow tracing the child's rounded rectangle, breathing on
   *   opacity alone. Keeps the child's shape legible, so use it wherever the
   *   outline IS the affordance.
   *
   * Treatment used to be implied by `fullWidth`, which conflated layout with
   * looks and left small controls stuck with the blob. The default preserves
   * that old pairing so existing callers are unchanged; pass it explicitly to
   * get the edge trace on something that isn't full width.
   * @default fullWidth ? 'edge' : 'glow'
   */
  variant?: 'glow' | 'edge';
  /**
   * Corner radius of the `edge` halo. Must match the child's own radius or the
   * trace sits proud of the corners instead of hugging them. Ignored by `glow`.
   * @default '2xl'
   */
  borderRadius?: string;
}

export const AiShimmer: React.FC<AiShimmerProps> = ({
  children,
  intensity = 'default',
  isActive = true,
  duration = 4.5,
  fullWidth = false,
  variant,
  borderRadius = '2xl',
}) => {
  const config = INTENSITY_MAP[intensity];
  const treatment = variant ?? (fullWidth ? 'edge' : 'glow');
  // Stops are sourced from the ACCENT ROLE via CSS custom properties so a brand
  // repaint propagates here. `color-mix` lets us keep the per-stop alpha-fade
  // while still pulling the hue from the token. (Browser support: Chrome 111+,
  // Safari 16.2+, Firefox 113+ — same baseline as canvas roundRect used in
  // capture animations.)
  //
  // MODE-INVARIANT ON PURPOSE, and this is the deliberate answer rather than an
  // accident of the rename. `accent.200/300/500` are raw palette steps, so this
  // halo is the same blue in both colour modes, unlike `icon.gradientStop*`
  // (which carry a `_dark` arm and do track it). Two reasons it stays that way:
  // the theme holds brand identity colours at one shade across modes — the
  // "brand identity colors are locked to the same shade" note in
  // semanticTokens.ts, which labels itself EXPERIMENT, so treat it as the
  // current house position rather than settled policy — and the pre-rebrand
  // `brand.500/300/200` were raw steps too, so mode-invariance is the behaviour
  // being restored, not a regression introduced by it. If that experiment is
  // ever resolved the other way, this halo moves with it. Checked on the dark canvas in a browser before keeping it.
  // Making it mode-aware would mean a new semantic triple, i.e. a restyle.
  //
  // `accent.*`, NOT the `brand.500/300/200` this shipped as — the halo had been
  // invisible since the 2026-08 identity landed. `brand` was a numeric ramp for
  // the old purple; the rebrand collapsed it into the `accent` role and kept
  // `brand.*` only as NAMED semantic tokens (brand.primary, brand.surface, …)
  // with no numeric steps. So `--chakra-colors-brand-500` resolved to nothing,
  // which makes the whole `color-mix()` invalid, which drops the `box-shadow` /
  // `background` declaration that uses it. Silent: no error, just no glow.
  // Guarded by `theme/__tests__/semanticTokenResolution.test.ts`.
  const inner = `color-mix(in srgb, var(--chakra-colors-accent-500) ${Math.round(
    60 * config.opacityMultiplier,
  )}%, transparent)`;
  const middle = `color-mix(in srgb, var(--chakra-colors-accent-300) ${Math.round(
    40 * config.opacityMultiplier,
  )}%, transparent)`;
  const outer = `color-mix(in srgb, var(--chakra-colors-accent-200) ${Math.round(
    20 * config.opacityMultiplier,
  )}%, transparent)`;

  return (
    <Box
      position="relative"
      display={fullWidth ? 'block' : 'inline-flex'}
      w={fullWidth ? 'full' : undefined}
    >
      {isActive &&
        (treatment === 'edge' ? (
          // Edge glow: a box-shadow that traces the child's rounded rectangle,
          // so the light stays tight to the border rather than filling a big
          // circle behind the whole element. Opacity-only breathing keeps it
          // from growing.
          <Box
            position="absolute"
            inset="0"
            borderRadius={borderRadius}
            pointerEvents="none"
            sx={{
              boxShadow: `0 0 ${config.edgeBlur} ${config.edgeSpread} ${inner}, 0 0 ${config.edgeInnerBlur} 0 ${middle}`,
              animation: `${edgeGlow} ${duration}s ease-in-out infinite`,
              // Non-essential delight — gate the pulse behind
              // `prefers-reduced-motion`; the static halo still renders.
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
              },
            }}
          />
        ) : (
          <Box
            position="absolute"
            inset={config.spread}
            borderRadius="xl"
            pointerEvents="none"
            sx={{
              background: `radial-gradient(circle, ${inner} 0%, ${middle} 40%, ${outer} 70%, transparent 100%)`,
              // The breathing animation is non-essential delight — gate it
              // behind `prefers-reduced-motion`. The static halo still
              // renders for everyone, so the glow signal isn't lost; only
              // the in/out scale + opacity pulse is suppressed.
              animation: `${shimmerGlow} ${duration}s ease-in-out infinite`,
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
              },
              filter: `blur(${config.blur})`,
            }}
          />
        ))}
      <Box position="relative" zIndex={1} w={fullWidth ? 'full' : undefined}>
        {children}
      </Box>
    </Box>
  );
};
